import {
    Component,
    ComponentInterface,
    h,
    Host,
    Prop,
    State,
} from "@stencil/core";
import api from "../../utils/api";
import { SearchPreviewResponse } from "./search-preview-response";

/** static base className */
const baseClass = "ux-iws-search-preview";

@Component({
    tag: "ux-iws-search-preview",
    styleUrl: "search-preview.scss",
})
export class SearchPreview implements ComponentInterface {
    /** the input field name */
    @Prop() searchPreviewFieldName: string = "";
    /** CSS-style selector for the search form element */
    @Prop() searchFormSelector: string = "";
    /** the input placeholder text */
    @Prop() inputPlaceholder: string = "";
    /** the submit button label */
    @Prop() buttonText: string = "";
    /** The base for building the request URL*/
    @Prop() baseUrl: string = "";
    /** Placeholder for search bar within dropdown element*/
    @Prop() dropdownPlaceholder: string = "";
    /** Label for dropdown element*/
    @Prop() dropdownLabel: string = "";
    /** Label for dropdown element*/
    @Prop() filterable: boolean = false;
    /** if set to true, the dropdown won't be rendered */
    @Prop() noDropdown: boolean = false;
    /** In case there is no dropdown, then we need a custom query param*/
    @Prop() customQueryParam: string = "";

    /** selector for merging master products on multiple calls */
    @Prop() masterProductMergeSelector: string = "";

    /** selector for merging cateegories on multiple calls */
    @Prop() categoryMergeSelector: string = "";

    /** selector for merging articles  on multiple calls */
    @Prop() articleMergeSelector: string = "";

    /** separator for splitting up value of select field */
    @Prop() multipleValueSeparator: string = "";

    /** predefined css class for dropdowncontainer */
    @Prop() dropDownContainerClass: string =
        "gr-4@large push-1@large gr-8@t push-2@t gr-12@m push-0@m u-marginBottom-20";

    /** predefined css class for searchBarContainer */
    @Prop() searchBarContainerClass: string =
        "gr-6@large push-1@large gr-8@t push-2@t gr-12@m push-0@m no-gutter@large";

    /** set Request Timeout */
    @Prop() requestTimeout: number = 500;

    /** Content from request to intershop */
    @State() shopContent: string = "";

    private callNumber: number = 0;
    private lastUsedCallNumber = 0;
    private timeoutID = 0;

    requestData() {
        const selectField = document.querySelector(
            ".ux-iws-search-preview .ux-radio-button__input:checked"
        ) as HTMLInputElement;

        const inputField = document.querySelector(
            'input[name="' + this.searchPreviewFieldName + '"]'
        ) as HTMLInputElement;

        let selectFieldValues: Array<string>;
        let selectFieldName: string;

        if (
            inputField == undefined ||
            (selectField == undefined && this.noDropdown == false)
        )
            return;

        if (inputField.value.trim() == "") {
            this.shopContent = "";
            return;
        }

        if (this.noDropdown === false) {
            selectFieldValues = this.getSelectFieldValues(selectField);
            selectFieldName = selectField.name;
        } else {
            const customSelectFieldName = this.customQueryParam.split("=")[0];
            const customSelectFieldValues = this.customQueryParam.split("=")[1];

            selectFieldName = customSelectFieldName;
            selectFieldValues = [`${customSelectFieldValues}`];
        }

        this.callNumber++;

        clearTimeout(this.timeoutID);
        this.timeoutID = window.setTimeout(() => {
            for (let selectFieldValue of selectFieldValues) {
                api<SearchPreviewResponse>(
                    this.getURL(
                        selectFieldName,
                        selectFieldValue,
                        inputField,
                        selectFieldValues.length > 1
                    )
                ).then((data) => {
                    if (
                        data.callNumber > this.lastUsedCallNumber ||
                        (this.baseUrl.indexOf("mock-data/") > -1 &&
                            selectFieldValues.length == 1)
                    ) {
                        //update latest content via response
                        this.lastUsedCallNumber = data.callNumber;
                        this.shopContent = data.searchResultHtml;
                    } else if (data.callNumber == this.lastUsedCallNumber) {
                        if (this.shopContent.trim() == "") {
                            this.shopContent = data.searchResultHtml;
                        } else {
                            this.mergeMultipleRequestWithSameCallNumber(data);
                        }
                    }
                });
            }
        }, this.requestTimeout);
    }

    getSelectFieldValues(selectField: HTMLInputElement) {
        let selectFieldValues = [selectField.value];

        if (this.multipleValueSeparator != "") {
            selectFieldValues = selectField.value.split(
                this.multipleValueSeparator
            );
        }

        return selectFieldValues;
    }

    mergeMultipleRequestWithSameCallNumber(data: SearchPreviewResponse) {
        var currentHTMLContainer = this.getWrappedContentWithContainer(
            this.shopContent
        );
        var htmlContainerForResponse = this.getWrappedContentWithContainer(
            data.searchResultHtml
        );

        this.addContainerOrAppendItems(
            currentHTMLContainer,
            htmlContainerForResponse,
            this.categoryMergeSelector
        );

        this.addContainerOrAppendItems(
            currentHTMLContainer,
            htmlContainerForResponse,
            this.masterProductMergeSelector
        );

        this.addContainerOrAppendItems(
            currentHTMLContainer,
            htmlContainerForResponse,
            this.articleMergeSelector
        );

        this.shopContent = currentHTMLContainer.innerHTML;
    }

    getWrappedContentWithContainer(htmlContent: string) {
        var container = document.createElement("div");

        container.innerHTML = htmlContent;
        return container;
    }

    addContainerOrAppendItems(
        currentContainer: HTMLElement,
        responseContainer: HTMLElement,
        contentSelector: string
    ) {
        var currentContent = currentContainer.querySelector(contentSelector);
        var responseContent = responseContainer.querySelector(contentSelector);

        if (currentContent.innerHTML.trim() == "") {
            currentContent.innerHTML = responseContent.innerHTML;
        } else {
            var currentItems = currentContainer.querySelector(
                contentSelector + "__items"
            );

            var additionalItems = responseContent.querySelector(
                contentSelector + "__items"
            );

            if (currentItems && additionalItems) {
                currentItems.insertAdjacentHTML(
                    "beforeend",
                    additionalItems.innerHTML
                );
            }
        }
    }

    getURL(
        selectFieldName: string,
        selectFieldValue: string,
        inputfield: HTMLInputElement,
        isMultipleCall: boolean
    ) {
        if (this.baseUrl.indexOf("mock-data/") > -1) {
            return `./mock-data/search-preview/search-preview-${selectFieldValue}.json`;
        }

        var baseURL =
            this.baseUrl.indexOf("?") > -1
                ? this.baseUrl + "&"
                : this.baseUrl + "?";

        return `${baseURL}${selectFieldName}=${selectFieldValue}&${inputfield.name}=${inputfield.value}&callNumber=${this.callNumber}&isMultipleCall=${isMultipleCall}`;
    }

    componentWillRender() {
        const isPreviewElementExisting = this.shopContent.includes(
            "ux-iws-search-preview__element"
        );

        if (!isPreviewElementExisting) {
            this.shopContent = "";
        }
    }

    render() {
        const requestData = () => {
            this.requestData();
        };

        return (
            <Host
                class={{
                    [`${baseClass}`]: true,
                    [`${baseClass}--active`]: this.shopContent !== "",
                }}
            >
                <div class="row">
                    {!this.noDropdown && (
                        <div class={`${this.dropDownContainerClass}`}>
                            <ux-dropdown
                                filterable={this.filterable}
                                onChange={requestData}
                                search-placeholder={this.dropdownPlaceholder}
                            >
                                <slot />
                            </ux-dropdown>
                        </div>
                    )}

                    <div
                        class={{
                            [`${this.searchBarContainerClass}`]: !this
                                .noDropdown,
                        }}
                    >
                        <ux-iws-search-bar
                            button-text={this.buttonText}
                            compact
                            onUxSearchBarReset={requestData}
                            name={this.searchPreviewFieldName}
                            onChange={requestData}
                            onKeyUp={requestData}
                            placeholder={this.inputPlaceholder}
                            search-form-selector={this.searchFormSelector}
                            noSticky={true}
                        ></ux-iws-search-bar>
                    </div>
                </div>
                <div class="row" innerHTML={this.shopContent} />
            </Host>
        );
    }
}
